import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import TextField from "@mui/material/TextField";
import Box from '@mui/material/Box';
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import {
  FloatInput,
} from "components/Input/Index.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import uuid from "react-uuid";
// require("dotenv").config();

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: '#fff',
    display: "flex",
  },
  tabs: {
    borderRight: `1px solid rgba(0, 0, 0, 0.12)`,
  },
  tab: {
    paddingLeft: "0rem",
    marginLeft: "0rem",
    flexDirection: "column",
    textAlign: "left",
    display: "grid",
    justifyContent: "left",
    alignItems: "flex-start",
    maxWidth: "3rem",
  },
  input: {
    display: "flex",
    flexDirection: "column",
    margin: "1rem 0rem 0rem",
  },
  inputTitle: {
    margin: "0.2rem 0rem 0.05rem 0.5rem",
    color: "#000000",
  },
  select: {
    display: "flex",
    flexDirection: "column",
    margin: "1rem 0.5rem",
  },
  switch: {
    marginLeft: "0.5rem",
  },
  textField: {
    fontSize: "14px",
    fontFamily: '"Source Sans Pro",sans-serif',
    "& .MuiInputBase-root": {
      height: "45px",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      fontFamily: '"Source Sans Pro",sans-serif',
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "14px",
      fontFamily: '"Source Sans Pro",sans-serif',
      transform: "translate(14px, 13px) scale(1)",
      color: "rgba(0, 0, 0, 0.54) !important",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
      color: "rgba(0, 0, 0, 0.54) !important",
    },
    '& .Mui-disabled': {
      color: "#000000",
    },
    "& .MuiTypography-colorTextSecondary": {
      color: "#000000 !important",
    },
    "& .MuiTypography-body2": {
      color: "#000000 !important",
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#605f70',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#605f70',
      },
    },
  },
  gridLatLng: {
    padding: "0px !important",
  }
}));

const autocompleteService = { current: null };
const geocoderService = { current: null };

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

export default function LocalizationAutocomplete(props) {
  const {
    inputTitle,
    value,
    validatedAddress,
    setValue,
    setValidatedAddress,
    inputValue,
    setInputValue,
    options,
    setOptions,
    loaded,
    specialWidth,
    displayLatLng,
    freeSolo,
  } = props;


  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  const fetchGeocode = useMemo(
    () =>
      throttle((request, callback) => {
        geocoderService.current.geocode(request, callback);
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!geocoderService.current && window.google && window.google.maps) {
      geocoderService.current = new window.google.maps.Geocoder();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions((value && typeof value === "object" && Object.keys(value).length !== 0) ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue, componentRestrictions: { country: 'fr' } }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);


  const classes = useStyles();

  return (
    <div className={classes.input}>
      {/* <div className={classes.inputTitle}>{inputTitle}</div> */}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12} className={classes.gridLatLng}>
          <Autocomplete
            disabled={props.disabled}
            id="google-map-demo"
            style={{ width: specialWidth }}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : (typeof option === "object" && option.description) ? option.description : ""
            }
            filterOptions={(x) => x}
            options={options}
            freeSolo={freeSolo}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            onChange={(event, newValue) => {
              console.log("on change");
              setOptions(newValue ? [newValue, ...options] : options);
              setValue(newValue);
              fetchGeocode({ placeId: newValue?.place_id }, (results) => {
                if (results?.length > 0) {
                  let result = results[0];
                  let newValidatedAddress = {};
                  let lat = result.geometry.location.lat();
                  let lng = result.geometry.location.lng();
                  let location = { lat, lng };
                  let geometry = { location };
                  let segmentedAddress = {};
                  result.address_components.forEach((component) => {
                    let types = component.types;
                    for (let i = 0; i < types.length; i++) {
                      let type = types[i];
                      if (type === "street_number") {
                        segmentedAddress.street_number = component.long_name;
                        break;
                      }
                      else if (type === "route") {
                        segmentedAddress.route = component.long_name;
                        break;
                      }
                      else if (type === "locality") {
                        segmentedAddress.town = component.long_name;
                        break;
                      }
                      else if (type === "country") {
                        segmentedAddress.country = component.long_name;
                        segmentedAddress.country_short = component.short_name;
                        break;
                      }
                      else if (type === "postal_code") {
                        segmentedAddress.postal_code = component.long_name;
                        break;
                      }
                    }
                  });
                  newValidatedAddress.segmentedAddress = segmentedAddress;
                  newValidatedAddress.geometry = geometry;
                  setValidatedAddress(newValidatedAddress);
                }
              });
            }}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              if(freeSolo && newInputValue)
              {
                 setValue({description:newInputValue});
              }
              console.log("on onInputChange",newInputValue===inputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                style={{ margin: "2px 8px" }}
                label={inputTitle}
                variant="outlined"
                fullWidth
                className={classes.textField}
              />
            )}
            renderOption={(props, option) => {
              const matches =
                option?.structured_formatting?.main_text_matched_substrings || [];
      
              const parts = option?.structured_formatting ? parse(
                option.structured_formatting.main_text,
                matches.map((match) => [match.offset, match.offset + match.length])) : [];
      
              return (
                <li {...props}>
                  <Grid container alignItems="center">
                    <Grid item sx={{ display: 'flex', width: 44 }}>
                      <LocationOnIcon sx={{ color: 'text.secondary' }} />
                    </Grid>
                    <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                      {parts.map((part, index) => (
                        <Box
                          key={index}
                          component="span"
                          sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                        >
                          {part.text}
                        </Box>
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        {option?.structured_formatting?.secondary_text || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
        </GridItem>
        {
          displayLatLng ?
            <>
              <GridItem xs={6} sm={6} md={6} className={classes.gridLatLng}>
                <FloatInput
                  disabled={props.disabled}
                  inputTitle={"Lat"}
                  floatInput={validatedAddress?.geometry?.location?.lat || ""}
                  handleFloatInputChange={(event, newValue) => {
                    if (!isNaN(event.target.value)) {
                      let newValidatedAddress = {};
                      newValidatedAddress.geometry = validatedAddress?.geometry || { location: { lat: "", lng: "" } };
                      newValidatedAddress.localization = validatedAddress?.localization || {};
                      newValidatedAddress.geometry.location.lat = Number(event.target.value);
                      setValidatedAddress(newValidatedAddress);
                    }
                  }
                  }
                />
              </GridItem>
              <GridItem xs={6} sm={6} md={6} className={classes.gridLatLng}>
                <FloatInput
                  disabled={props.disabled}
                  inputTitle={"Lng"}
                  floatInput={validatedAddress?.geometry?.location?.lng || ""}
                  handleFloatInputChange={(event, newValue) => {
                    if (!isNaN(event.target.value)) {
                      let newValidatedAddress = {};
                      newValidatedAddress.geometry = validatedAddress?.geometry || { location: { lat: "", lng: "" } };
                      newValidatedAddress.localization = validatedAddress?.localization || {};
                      newValidatedAddress.geometry.location.lng = Number(event.target.value);
                      setValidatedAddress(newValidatedAddress);
                    }
                  }
                  }
                />
              </GridItem>
            </> : <></>
        }
      </GridContainer>
    </div>
  );
}
LocalizationAutocomplete.defaultProps = {
  specialWidth: "99%",
  displayLatLng: true,
};

LocalizationAutocomplete.propTypes = {
  inputTitle: PropTypes.any,
  value: PropTypes.any,
  setValue: PropTypes.any,
  inputValue: PropTypes.string,
  setInputValue: PropTypes.func,
  options: PropTypes.array,
  setOptions: PropTypes.func,
  loaded: PropTypes.object,
  specialWidth: PropTypes.string,
  displayLatLng: PropTypes.any,
};
